import type {
  WebsiteBlockManifest,
  WebsitePageKind,
  WebsiteSlotSchema,
  WebsiteSlotSemantic,
  WebsiteTemplateManifest,
} from '../../types/website-templates';
import { WEBSITE_PAGE_KINDS, WEBSITE_RETIRED_SLOT_SEMANTICS } from '../../types/website-templates';

// Edit regions stay a leaf over the types module (the Beauty offline harness
// loads this file with that one dependency), so the retired check reads the
// shared list directly instead of importing `./internal`.
const RETIRED_SEMANTICS: ReadonlySet<string> = new Set(WEBSITE_RETIRED_SLOT_SEMANTICS);
const isRetiredSlot = (slot: WebsiteSlotSchema): boolean => RETIRED_SEMANTICS.has(slot.semantic);

/**
 * C2 edit regions (A21). One marker and one dialog per region, not per slot.
 *
 * Regions are DERIVED from the manifest: its block order, the item path of
 * each slot id and the slot semantics. Nothing is stored, so immutable v1
 * manifests and frozen legacy templates get regions without a data change.
 * The server authority stays per slot: a region is only a grouping of slot
 * ids that `save_store_website_slots` validates one by one.
 */
export type WebsiteEditRegionKind = 'text' | 'rich-text' | 'media' | 'reference' | 'newsletter';
export type WebsiteEditRegionScope = 'chrome' | WebsitePageKind;

/** Closed label ids → `admin.websiteTemplates.region.<id>.label` / `.edit`. */
export type WebsiteRegionLabelId =
  | 'heroText'
  | 'heroMedia'
  | 'promoText'
  | 'promoMedia'
  | 'aboutText'
  | 'aboutMedia'
  | 'categoryImage'
  | 'categoryTile'
  | 'sectionHeading'
  | 'sectionText'
  | 'featureText'
  | 'announcement'
  | 'tagline'
  | 'footerText'
  | 'storeDetails'
  | 'storeAbout'
  | 'deliveryPolicy'
  | 'returnsPolicy'
  | 'productCare'
  | 'productSizeGuide'
  | 'cartNote'
  | 'checkoutNote'
  | 'thankYouMessage'
  | 'legacyText'
  | 'legacyMedia'
  | 'newsletterCollection'
  /** Epic #228 P2: the heading of a category tiles block, its only editable item. */
  | 'categoriesHeading'
  /**
   * Epic #228 P6 (PD9): the two media of a hero that has both `media` and
   * `desktopMedia` (Luxury, Beauty), named by device instead of by ordinal.
   */
  | 'heroMediaMobile'
  | 'heroMediaDesktop';

export const WEBSITE_REGION_LABEL_IDS: readonly WebsiteRegionLabelId[] = [
  'heroText',
  'heroMedia',
  'promoText',
  'promoMedia',
  'aboutText',
  'aboutMedia',
  'categoryImage',
  'categoryTile',
  'sectionHeading',
  'sectionText',
  'featureText',
  'announcement',
  'tagline',
  'footerText',
  'storeDetails',
  'storeAbout',
  'deliveryPolicy',
  'returnsPolicy',
  'productCare',
  'productSizeGuide',
  'cartNote',
  'checkoutNote',
  'thankYouMessage',
  'legacyText',
  'legacyMedia',
  'newsletterCollection',
  'categoriesHeading',
  'heroMediaMobile',
  'heroMediaDesktop',
];

export interface WebsiteEditRegion {
  /** `text:<itemPath>` | `rich:<slotId>` | `media:<slotId>` | `ref:<slotId>`. */
  readonly regionId: string;
  readonly kind: WebsiteEditRegionKind;
  readonly scope: WebsiteEditRegionScope;
  readonly blockId: string;
  /**
   * Manifest order. text: slots of type text|link; every other kind: exactly
   * 1, except a media region whose slot is `clickable` (epic #228 P5): the
   * media slot, then its link slot.
   */
  readonly slotIds: readonly string[];
  /**
   * Slots that render a box (links render none). Empty → the block root.
   * Regions of a `legacy.enginePage` block are never anchored, whatever this
   * list holds (C8 anchoring rule).
   */
  readonly anchorSlotIds: readonly string[];
  readonly labelId: WebsiteRegionLabelId;
  /** The first member's ordinal. */
  readonly ordinal?: number;
}

export const WEBSITE_EDIT_REGION_ID_RE =
  /^(text|rich|media|ref|newsletter):[a-z][a-zA-Z0-9]*(\.[a-zA-Z0-9-]+){0,6}$/;
export const WEBSITE_EDIT_REGION_ID_MAX_LENGTH = 107;
export const WEBSITE_TEXT_REGION_MAX_SLOTS = 8;

const REGION_ID_PREFIX: Readonly<Record<WebsiteEditRegionKind, string>> = {
  text: 'text',
  'rich-text': 'rich',
  media: 'media',
  reference: 'ref',
  newsletter: 'newsletter',
};

export function isWebsiteEditRegionId(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length <= WEBSITE_EDIT_REGION_ID_MAX_LENGTH &&
    WEBSITE_EDIT_REGION_ID_RE.test(value)
  );
}

export interface WebsiteEditRegionIdParts {
  readonly kind: WebsiteEditRegionKind;
  /** text: the item path shared by the members. Every other kind: the slot id. */
  readonly path: string;
}

/** Reads a region id without a manifest. It proves the shape only, not that the region exists. */
export function parseWebsiteEditRegionId(regionId: unknown): WebsiteEditRegionIdParts | null {
  if (!isWebsiteEditRegionId(regionId)) return null;
  const separator = regionId.indexOf(':');
  const prefix = regionId.slice(0, separator);
  // The id grammar admits these four prefixes only.
  const kind: WebsiteEditRegionKind =
    prefix === REGION_ID_PREFIX.text
      ? 'text'
      : prefix === REGION_ID_PREFIX['rich-text']
        ? 'rich-text'
        : prefix === REGION_ID_PREFIX.media
          ? 'media'
          : prefix === REGION_ID_PREFIX.newsletter
            ? 'newsletter'
            : 'reference';
  return { kind, path: regionId.slice(separator + 1) };
}

/** The slot id without its last segment: `home.hero.slide2.heading` → `home.hero.slide2`. */
export function websiteSlotItemPath(slotId: string): string {
  const lastDot = slotId.lastIndexOf('.');
  return lastDot === -1 ? slotId : slotId.slice(0, lastDot);
}

function mediaLabel(semantic: WebsiteSlotSemantic): WebsiteRegionLabelId {
  switch (semantic) {
    case 'hero.heading':
    case 'hero.subheading':
    case 'hero.ctaLabel':
    case 'hero.ctaLink':
    case 'hero.media':
      return 'heroMedia';
    case 'promo.heading':
    case 'promo.body':
    case 'promo.ctaLabel':
    case 'promo.ctaLink':
    case 'promo.media':
      return 'promoMedia';
    case 'about.heading':
    case 'about.body':
    case 'about.media':
      return 'aboutMedia';
    case 'category.tile':
    case 'category.tileImage':
      return 'categoryImage';
    case 'announcement.text':
    case 'brand.tagline':
    case 'section.heading':
    case 'section.body':
    case 'feature.title':
    case 'feature.body':
    case 'policy.delivery':
    case 'policy.returns':
    case 'contact.note':
    case 'product.careNote':
    case 'product.sizeGuide':
    case 'cart.note':
    case 'checkout.note':
    case 'confirmation.message':
    case 'footer.note':
    case 'mission.body':
    case 'mission.ctaLabel':
    case 'mission.ctaLink':
    case 'community.heading':
    case 'community.media':
    case 'community.ctaLabel':
    case 'community.ctaLink':
    case 'newsletter.heading':
    case 'newsletter.body':
    case 'newsletter.collection':
    case 'legacy.text':
    case 'legacy.media':
      // The neutral label ("Image"): no template declares media with these.
      return 'legacyMedia';
  }
}

function richTextLabel(semantic: WebsiteSlotSemantic): WebsiteRegionLabelId {
  switch (semantic) {
    case 'about.body':
      return 'storeAbout';
    case 'policy.delivery':
      return 'deliveryPolicy';
    case 'policy.returns':
      return 'returnsPolicy';
    case 'product.careNote':
      return 'productCare';
    case 'product.sizeGuide':
      return 'productSizeGuide';
    case 'announcement.text':
    case 'brand.tagline':
    case 'hero.heading':
    case 'hero.subheading':
    case 'hero.ctaLabel':
    case 'hero.ctaLink':
    case 'hero.media':
    case 'category.tile':
    case 'category.tileImage':
    case 'section.heading':
    case 'section.body':
    case 'feature.title':
    case 'feature.body':
    case 'promo.heading':
    case 'promo.body':
    case 'promo.media':
    case 'promo.ctaLabel':
    case 'promo.ctaLink':
    case 'about.heading':
    case 'about.media':
    case 'contact.note':
    case 'cart.note':
    case 'checkout.note':
    case 'confirmation.message':
    case 'footer.note':
    case 'mission.body':
    case 'mission.ctaLabel':
    case 'mission.ctaLink':
    case 'community.heading':
    case 'community.media':
    case 'community.ctaLabel':
    case 'community.ctaLink':
    case 'newsletter.heading':
    case 'newsletter.body':
    case 'newsletter.collection':
    case 'legacy.text':
    case 'legacy.media':
      return 'sectionText';
  }
}

function textLabel(
  semantic: WebsiteSlotSemantic,
  scope: WebsiteEditRegionScope,
): WebsiteRegionLabelId {
  switch (semantic) {
    case 'hero.heading':
    case 'hero.subheading':
    case 'hero.ctaLabel':
    case 'hero.ctaLink':
    case 'hero.media':
      return 'heroText';
    case 'promo.heading':
    case 'promo.body':
    case 'promo.ctaLabel':
    case 'promo.ctaLink':
    case 'promo.media':
      return 'promoText';
    case 'feature.title':
    case 'feature.body':
      return 'featureText';
    case 'about.heading':
    case 'about.body':
    case 'about.media':
      return scope === 'store-info' ? 'storeDetails' : 'aboutText';
    case 'section.heading':
      return 'sectionHeading';
    case 'section.body':
      return 'sectionText';
    case 'announcement.text':
      return 'announcement';
    case 'brand.tagline':
      return 'tagline';
    case 'footer.note':
      return 'footerText';
    case 'contact.note':
    case 'policy.delivery':
    case 'policy.returns':
      return 'storeDetails';
    case 'cart.note':
      return 'cartNote';
    case 'checkout.note':
      return 'checkoutNote';
    case 'confirmation.message':
      return 'thankYouMessage';
    case 'product.careNote':
    case 'product.sizeGuide':
      return 'sectionText';
    case 'category.tile':
    case 'category.tileImage':
      return 'categoryTile';
    case 'mission.body':
    case 'mission.ctaLabel':
    case 'mission.ctaLink':
    case 'community.heading':
    case 'community.media':
    case 'community.ctaLabel':
    case 'community.ctaLink':
    case 'newsletter.heading':
    case 'newsletter.body':
    case 'newsletter.collection':
      return 'sectionText';
    case 'legacy.text':
    case 'legacy.media':
      return 'legacyText';
  }
}

function regionKindOf(slot: WebsiteSlotSchema): WebsiteEditRegionKind {
  switch (slot.type) {
    case 'text':
    case 'link':
      return 'text';
    case 'rich-text':
      return 'rich-text';
    case 'media':
      return 'media';
    case 'newsletter-collection':
      return 'newsletter';
    case 'category-ref':
    case 'product-ref':
    case 'custom-collection-ref':
      return 'reference';
  }
}

function labelOf(
  kind: WebsiteEditRegionKind,
  semantic: WebsiteSlotSemantic,
  scope: WebsiteEditRegionScope,
): WebsiteRegionLabelId {
  switch (kind) {
    case 'newsletter':
      return 'newsletterCollection';
    case 'media':
      return mediaLabel(semantic);
    case 'reference':
      return 'categoryTile';
    case 'rich-text':
      return richTextLabel(semantic);
    case 'text':
      return textLabel(semantic, scope);
  }
}

interface RegionDraft {
  readonly regionId: string;
  readonly kind: WebsiteEditRegionKind;
  readonly blockId: string;
  readonly component: string;
  readonly first: WebsiteSlotSchema;
  readonly slotIds: string[];
  readonly anchorSlotIds: string[];
  /** Epic #228 P6 (PD9): a device-named hero media label; no ordinal. */
  readonly deviceLabel?: WebsiteRegionLabelId;
}

/**
 * Epic #228 P6 (PD9): `<item>.media` and `<item>.desktopMedia`, both
 * `hero.media` slots of the same block, are the mobile and desktop media of
 * one hero (`media` also shows on desktop while `desktopMedia` is empty).
 * Read from the manifest only, so this file stays a leaf: the frame registry
 * (`media-frames.ts`) agrees, and `media-frames.test.ts` proves it for every
 * catalog manifest. Every other media region keeps its label and ordinal.
 */
function heroMediaDeviceLabel(
  slot: WebsiteSlotSchema,
  block: WebsiteBlockManifest,
  schemaById: ReadonlyMap<string, WebsiteSlotSchema>,
): WebsiteRegionLabelId | undefined {
  if (slot.type !== 'media' || slot.semantic !== 'hero.media') return undefined;
  const item = websiteSlotItemPath(slot.id);
  const isHeroMedia = (slotId: string): boolean => {
    const sibling = schemaById.get(slotId);
    return (
      sibling?.type === 'media' &&
      sibling.semantic === 'hero.media' &&
      !isRetiredSlot(sibling) &&
      block.slotIds.includes(slotId)
    );
  };
  if (slot.id === `${item}.desktopMedia` && isHeroMedia(`${item}.media`)) return 'heroMediaDesktop';
  if (slot.id === `${item}.media` && isHeroMedia(`${item}.desktopMedia`)) return 'heroMediaMobile';
  return undefined;
}

function blocksOf(
  manifest: WebsiteTemplateManifest,
  scope: WebsiteEditRegionScope,
): readonly WebsiteBlockManifest[] {
  return scope === 'chrome' ? manifest.chrome : manifest.pages[scope].blocks;
}

/**
 * The regions of one scope, in manifest order. Total and deterministic: a
 * slot id the manifest does not define is skipped, and a text item with more
 * slots than `WEBSITE_TEXT_REGION_MAX_SLOTS` is kept whole, never split.
 * `listWebsiteEditRegionIssues` reports it so catalog tooling refuses it.
 * A slot with a retired semantic (epic #228 P2: category tiles) gets no
 * region: the category itself owns it.
 */
/**
 * Epic #228 P5 (PD8 seam): link slot id → the media slot whose image anchors
 * it (`clickable`). The link is edited in that media region, not in the
 * item's text region. When two media share one link, the first media in
 * block order owns it. No catalog manifest declares `clickable` yet.
 */
function clickableLinkOwners(
  blocks: readonly WebsiteBlockManifest[],
  schemaById: ReadonlyMap<string, WebsiteSlotSchema>,
): ReadonlyMap<string, string> {
  const owners = new Map<string, string>();
  for (const block of blocks) {
    for (const slotId of block.slotIds) {
      const slot = schemaById.get(slotId);
      const linkSlotId = slot?.type === 'media' ? slot.clickable?.linkSlotId : undefined;
      if (!slot || isRetiredSlot(slot) || linkSlotId === undefined) continue;
      const link = schemaById.get(linkSlotId);
      if (
        link?.type === 'link' &&
        !isRetiredSlot(link) &&
        block.slotIds.includes(linkSlotId) &&
        !owners.has(linkSlotId)
      ) {
        owners.set(linkSlotId, slotId);
      }
    }
  }
  return owners;
}

export function deriveWebsiteEditRegions(
  manifest: WebsiteTemplateManifest,
  scope: WebsiteEditRegionScope,
): readonly WebsiteEditRegion[] {
  const schemaById = new Map(manifest.slots.map((slot) => [slot.id, slot]));
  const drafts: RegionDraft[] = [];
  const textDrafts = new Map<string, RegionDraft>();
  const blocks = blocksOf(manifest, scope);
  const linkOwners = clickableLinkOwners(blocks, schemaById);
  const mediaLinks = new Map([...linkOwners].map(([linkSlotId, mediaId]) => [mediaId, linkSlotId]));

  for (const block of blocks) {
    for (const slotId of block.slotIds) {
      const slot = schemaById.get(slotId);
      if (!slot || isRetiredSlot(slot)) continue;
      // The media region of its `clickable` image edits this link.
      if (linkOwners.has(slotId)) continue;
      const kind = regionKindOf(slot);

      if (kind !== 'text') {
        const anchoredLink = mediaLinks.get(slotId);
        const deviceLabel = heroMediaDeviceLabel(slot, block, schemaById);
        drafts.push({
          regionId: `${REGION_ID_PREFIX[kind]}:${slotId}`,
          kind,
          blockId: block.id,
          component: block.component,
          first: slot,
          slotIds: anchoredLink === undefined ? [slotId] : [slotId, anchoredLink],
          anchorSlotIds: [slotId],
          ...(deviceLabel === undefined ? {} : { deviceLabel }),
        });
        continue;
      }

      const regionId = `${REGION_ID_PREFIX.text}:${websiteSlotItemPath(slotId)}`;
      let draft = textDrafts.get(regionId);
      if (!draft) {
        draft = {
          regionId,
          kind,
          blockId: block.id,
          component: block.component,
          first: slot,
          slotIds: [],
          anchorSlotIds: [],
        };
        textDrafts.set(regionId, draft);
        drafts.push(draft);
      }
      draft.slotIds.push(slotId);
      if (slot.type !== 'link') draft.anchorSlotIds.push(slotId);
    }
  }

  return drafts.map((draft) => {
    // Epic #228 P2: the heading of a category tiles block is the block's only
    // editable item, so it is named for the block and carries no ordinal.
    const categoriesHeading =
      draft.kind === 'text' &&
      draft.component === 'home.categoryTiles' &&
      draft.first.semantic === 'section.heading';
    return {
      regionId: draft.regionId,
      kind: draft.kind,
      scope,
      blockId: draft.blockId,
      slotIds: draft.slotIds,
      anchorSlotIds: draft.anchorSlotIds,
      labelId: categoriesHeading
        ? 'categoriesHeading'
        : (draft.deviceLabel ?? labelOf(draft.kind, draft.first.semantic, scope)),
      ...(categoriesHeading || draft.deviceLabel !== undefined || draft.first.ordinal === undefined
        ? {}
        : { ordinal: draft.first.ordinal }),
    };
  });
}

/** chrome ∪ page, chrome first: what one canvas page can show. */
export function deriveWebsitePageRegions(
  manifest: WebsiteTemplateManifest,
  pageKind: WebsitePageKind,
): readonly WebsiteEditRegion[] {
  return [
    ...deriveWebsiteEditRegions(manifest, 'chrome'),
    ...deriveWebsiteEditRegions(manifest, pageKind),
  ];
}

const ALL_SCOPES: readonly WebsiteEditRegionScope[] = ['chrome', ...WEBSITE_PAGE_KINDS];

function allRegions(manifest: WebsiteTemplateManifest): WebsiteEditRegion[] {
  return ALL_SCOPES.flatMap((scope) => deriveWebsiteEditRegions(manifest, scope));
}

export function findWebsiteEditRegion(
  manifest: WebsiteTemplateManifest,
  regionId: string,
): WebsiteEditRegion | null {
  if (!isWebsiteEditRegionId(regionId)) return null;
  return allRegions(manifest).find((region) => region.regionId === regionId) ?? null;
}

/** "Go to item" for publish issues. */
export function websiteRegionForSlot(
  manifest: WebsiteTemplateManifest,
  slotId: string,
): WebsiteEditRegion | null {
  return allRegions(manifest).find((region) => region.slotIds.includes(slotId)) ?? null;
}

/**
 * What catalog tooling must refuse. Empty for a manifest whose regions are
 * all well-formed. Closed issue shapes:
 * `region:<regionId>:too_many_slots`, `region:<regionId>:id`,
 * `region:<regionId>:duplicate` and `slot:<slotId>:no_region`. A slot with
 * a retired semantic needs no region. A link slot that a `clickable` media
 * slot anchors is covered by that media region (epic #228 P5).
 */
export function listWebsiteEditRegionIssues(manifest: WebsiteTemplateManifest): readonly string[] {
  const issues: string[] = [];
  const seenRegions = new Set<string>();
  const covered = new Set<string>();

  for (const region of allRegions(manifest)) {
    if (!isWebsiteEditRegionId(region.regionId)) issues.push(`region:${region.regionId}:id`);
    if (seenRegions.has(region.regionId)) issues.push(`region:${region.regionId}:duplicate`);
    seenRegions.add(region.regionId);
    if (region.kind === 'text' && region.slotIds.length > WEBSITE_TEXT_REGION_MAX_SLOTS) {
      issues.push(`region:${region.regionId}:too_many_slots`);
    }
    for (const slotId of region.slotIds) covered.add(slotId);
  }
  for (const slot of manifest.slots) {
    if (isRetiredSlot(slot)) continue;
    if (!covered.has(slot.id)) issues.push(`slot:${slot.id}:no_region`);
  }
  return issues;
}
